import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ChevronLeft, History, Loader2, MessageSquareText, Phone, Search, UserPlus, UsersRound, Video } from "@fg/icons";
import { connectParityApi, normalizeUserId, type ChatListItem } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import {
    useChatReactions,
    useConversationMembers,
    useConversationMessages,
    useDirectChatMessages,
    usePinToggle,
    usePins,
    useSendConversationMessage,
    useSendDirectChatMessage
} from "@/api/hooks";
import { placeCall } from "@/lib/calls";
import { flashMessage } from "@/lib/jump";
import { cn } from "@/lib/utils";
import { ChatMessageContent } from "../ChatMessageContent";
import { DropOverlay } from "../files/ComposerAttachments";
import { ConversationFiles } from "../files/ConversationFiles";
import { useDriveAttachments, useFileDropZone } from "../files/useDriveAttachments";
import { PinnedBar } from "../pins/PinnedBar";
import { StatusNoteText, usePeerStatus } from "../presence/PeerStatus";
import { ConversationFindBar } from "../search/ConversationFindBar";
import { AddPeopleDialog } from "./AddPeopleDialog";
import { GroupManageDialog } from "./GroupManageDialog";
import { ChatComposer, type ChatShareTarget, type MentionCandidate } from "./ChatComposer";
import { ChatAvatar, dayLabel, presenceLabel, presenceTextClass, sameDay, timeLabel, typingText } from "./chatUi";

interface ThreadMessage {
    id: string;
    authorUserId: string;
    authorEmail: string;
    authorDisplayName: string | null;
    bodyHtml: string;
    isEdited: boolean;
    sentAt: string;
}

type Row =
    | { type: "day"; key: string; label: string }
    | { type: "new"; key: string }
    | { type: "msg"; key: string; m: ThreadMessage; mine: boolean; first: boolean };

/** A run of messages from one person within this many ms reads as one block. */
const GROUP_GAP_MS = 5 * 60_000;

/**
 * One 1:1 or group chat, Teams-style: your messages on the right, grouped by
 * author with date separators and a "New messages" line, a jump-to-latest
 * pill that doesn't yank you down while you read history, a typing line,
 * and call buttons for both kinds. The old DM and group views each had
 * their own layout (plain rows vs bordered cards), full timestamps on every
 * message, and scrolled to the bottom on every update.
 *
 * "Chat | Files" tabs, as in Teams: Files lists everything shared here. A
 * 1:1 chat shows the other person's status message under their name, and
 * above the composer when they asked for it ("Show when people message me").
 */
export function ChatThread({ chat, presence, typingNames, onTyping, onBack }: {
    chat: ChatListItem;
    presence: string | undefined;
    typingNames: string[];
    onTyping: (started: boolean) => void;
    onBack: () => void;
}) {
    const isDm = chat.kind === "dm";
    const auth = useAuth();
    const me = normalizeUserId(auth.user?.profile.sub);
    const token = useAccessToken();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [tab, setTab] = useState<"chat" | "files">("chat");
    const { noteOf } = usePeerStatus();
    const peerNote = isDm ? noteOf(chat.peerUserId) : null;
    // Jumping to a search hit or pin that isn't loaded shows the messages
    // around it (`anchor`) until "Jump to latest".
    const [anchor, setAnchor] = useState<string | null>(null);
    const [pendingJump, setPendingJump] = useState<string | null>(null);
    const [findOpen, setFindOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);

    const dmMessages = useDirectChatMessages(isDm ? chat.id : null, anchor);
    const groupMessages = useConversationMessages(isDm ? null : chat.id, anchor);
    const members = useConversationMembers(isDm ? null : chat.id);
    const sendDm = useSendDirectChatMessage(chat.id);
    const sendGroup = useSendConversationMessage(chat.id);
    const reactions = useChatReactions(isDm ? "dms" : "conversations", chat.id);
    const query = isDm ? dmMessages : groupMessages;
    const messages = useMemo(() => (query.data ?? []) as ThreadMessage[], [query.data]);

    // Pins: anyone in a chat may pin (max 25, enforced by the BFF).
    const pinScope = isDm ? "dms" : "conversations";
    const pins = usePins(pinScope, chat.id);
    const pinToggle = usePinToggle(pinScope, chat.id);
    const pinnedIds = useMemo(() => new Set((pins.data?.pins ?? []).map((p) => p.messageId)), [pins.data]);

    const jumpTo = (messageId: string) => {
        setTab("chat");
        setPendingJump(messageId);
        if (!messages.some((m) => m.id === messageId)) setAnchor(messageId);
    };
    useEffect(() => {
        if (!pendingJump) return;
        if (messages.some((m) => m.id === pendingJump)) {
            const id = pendingJump;
            setPendingJump(null);
            window.requestAnimationFrame(() => flashMessage(`msg-${id}`));
        } else if (query.isSuccess && !query.isFetching && anchor === pendingJump) {
            // Not there even around itself — deleted since. Stop waiting.
            setPendingJump(null);
        }
    }, [pendingJump, messages, query.isSuccess, query.isFetching, anchor]);

    // Where the "New messages" line goes: before the Nth-newest message from
    // someone else, N being the unread count when the chat was opened.
    const unreadAtOpen = useRef(chat.unreadCount);
    const dividerBeforeId = useMemo(() => {
        const n = unreadAtOpen.current;
        if (n <= 0) return null;
        let seen = 0;
        for (let i = messages.length - 1; i >= 0; i--) {
            if (normalizeUserId(messages[i]!.authorUserId) !== me && ++seen === n) return messages[i]!.id;
        }
        return null;
    }, [messages, me]);

    const rows = useMemo<Row[]>(() => {
        const out: Row[] = [];
        let prev: ThreadMessage | null = null;
        for (const m of messages) {
            const at = new Date(m.sentAt);
            const prevAt = prev ? new Date(prev.sentAt) : null;
            const newDay = !prevAt || !sameDay(prevAt, at);
            if (newDay) out.push({ type: "day", key: `day-${m.id}`, label: dayLabel(at) });
            if (m.id === dividerBeforeId) out.push({ type: "new", key: "new" });
            const first = newDay
                || m.id === dividerBeforeId
                || prev!.authorUserId !== m.authorUserId
                || at.getTime() - prevAt!.getTime() > GROUP_GAP_MS;
            out.push({ type: "msg", key: m.id, m, mine: normalizeUserId(m.authorUserId) === me, first });
            prev = m;
        }
        return out;
    }, [messages, me, dividerBeforeId]);

    // Mark read on open and as messages arrive, while the tab is visible.
    const markRead = useCallback(() => {
        if (!token || document.visibilityState !== "visible") return;
        const done = isDm
            ? connectParityApi.markDmRead(chat.id, token)
            : connectParityApi.markConversationRead(chat.id, token);
        void Promise.resolve(done)
            .then(() => qc.invalidateQueries({ queryKey: ["chats"] }))
            .catch(() => undefined);
    }, [token, isDm, chat.id, qc]);
    useEffect(() => { markRead(); }, [markRead, messages.length]);
    useEffect(() => {
        const onVisible = () => markRead();
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [markRead]);

    // Scrolling: land on the "New messages" line (or the bottom) when opened;
    // afterwards follow new messages only if you're already at the bottom or
    // they're yours — otherwise count them on the jump pill.
    const scrollerRef = useRef<HTMLDivElement>(null);
    const atBottomRef = useRef(true);
    const [showJump, setShowJump] = useState(false);
    const [newBelow, setNewBelow] = useState(0);
    const seenCount = useRef<number | null>(null);
    // A different window of messages (anchored or back to latest) lands like a fresh open.
    useEffect(() => { seenCount.current = null; }, [anchor]);
    useLayoutEffect(() => {
        const el = scrollerRef.current;
        if (!el || messages.length === 0) return;
        if (seenCount.current === null) {
            seenCount.current = messages.length;
            const divider = el.querySelector("[data-new-divider]");
            if (divider) divider.scrollIntoView({ block: "center" });
            else el.scrollTop = el.scrollHeight;
            return;
        }
        const added = messages.length - seenCount.current;
        seenCount.current = messages.length;
        if (added <= 0) return;
        const lastMine = normalizeUserId(messages[messages.length - 1]!.authorUserId) === me;
        if (atBottomRef.current || lastMine) {
            el.scrollTop = el.scrollHeight;
            setNewBelow(0);
        } else {
            setNewBelow((n) => n + added);
        }
    }, [messages, me]);

    const onScroll = () => {
        const el = scrollerRef.current;
        if (!el) return;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        atBottomRef.current = atBottom;
        setShowJump(!atBottom);
        if (atBottom) setNewBelow(0);
    };

    const jumpToLatest = () => {
        setNewBelow(0);
        if (anchor) {
            setAnchor(null);
            return;
        }
        scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
    };

    const send = async (html: string) => {
        // Your new message shows at the bottom of the latest messages.
        setAnchor(null);
        if (isDm) await sendDm.mutateAsync(html);
        else await sendGroup.mutateAsync(html);
        void qc.invalidateQueries({ queryKey: ["chats"] });
    };

    const others = (members.data ?? []).filter((m) => normalizeUserId(m.userId) !== me);

    // Files: attached, pasted or dropped anywhere on the thread. They go to
    // the sender's Drive and everyone in the chat gets edit access by default
    // (Teams' "People in this chat can edit"). The thread is keyed per chat,
    // so unsent files never follow you into another conversation.
    const attachments = useDriveAttachments("edit");
    const threadDrop = useFileDropZone(attachments.addFiles);
    const resolveShare = async (): Promise<ChatShareTarget> => {
        const ownerEmail = (auth.user?.profile.email ?? "").toLowerCase();
        if (isDm) {
            return { emails: chat.peerEmail ? [chat.peerEmail] : [], ownerEmail, source: "connect-chat", sourceRef: chat.id };
        }
        // The member list may not have loaded yet — fetch it rather than share with nobody.
        const list = members.data ?? (await members.refetch()).data ?? [];
        return {
            emails: list.filter((m) => normalizeUserId(m.userId) !== me).map((m) => m.email),
            ownerEmail,
            source: "connect-chat",
            sourceRef: chat.id
        };
    };

    const [calling, setCalling] = useState(false);
    const call = async (kind: "audio" | "video") => {
        const invitees = isDm ? (chat.peerEmail ? [chat.peerEmail] : []) : others.map((m) => m.email).filter(Boolean);
        if (invitees.length === 0 || calling) return;
        setCalling(true);
        try {
            await placeCall({
                kind,
                subject: isDm
                    ? `${kind === "video" ? "Video" : "Audio"} call with ${chat.title}`
                    : `${kind === "video" ? "Video" : "Audio"} call — ${chat.title}`,
                invitees,
                calleeLabel: chat.title,
                token,
                navigate
            });
        } catch (err) {
            console.warn("Call failed", err);
        } finally {
            setCalling(false);
        }
    };

    const typing = typingText(typingNames);
    const canCall = isDm ? Boolean(chat.peerEmail) : others.length > 0;

    // Who "@" offers first: the other person, or the group's other members.
    const mentionCandidates = useMemo<MentionCandidate[]>(() => isDm
        ? (chat.peerUserId ? [{ userId: chat.peerUserId, name: chat.title, email: chat.peerEmail ?? "" }] : [])
        : others.map((m) => ({ userId: m.userId, name: m.displayName || m.email, email: m.email })),
    // `others` is rebuilt every render; its members are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDm, chat.peerUserId, chat.title, chat.peerEmail, members.data, me]);

    const tabs = [
        { id: "chat" as const, label: "Chat" },
        { id: "files" as const, label: "Files" }
    ];
    const onTabKey = (e: React.KeyboardEvent) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const next = tab === "chat" ? "files" : "chat";
        setTab(next);
        document.getElementById(`chat-tab-${next}`)?.focus();
    };

    return (
        <section className="flex min-h-0 min-w-0 flex-1 flex-col" aria-label={chat.title}>
            {/* Header per the suite-shell mock-up: avatar + presence, name and
                coloured status, Chat | Files tabs with an accent underline, then
                search, the primary Connect (video call) button and audio call. */}
            <header className="flex h-14 shrink-0 items-center gap-2.5 border-b border-[var(--connect-line)] px-3 sm:px-3.5">
                <button
                    type="button"
                    onClick={onBack}
                    aria-label="Back to chats"
                    className="flex h-8 w-8 items-center justify-center rounded hover:bg-[var(--connect-hover)] md:hidden"
                >
                    <ChevronLeft className="h-5 w-5" />
                </button>
                <ChatAvatar name={chat.title} group={!isDm} presence={isDm ? presence ?? "offline" : undefined} size="sm" />
                <div className="min-w-0 flex-1 sm:flex-initial">
                    {isDm ? <h1 className="truncate text-sm font-semibold">{chat.title}</h1> : (
                        <h1 className="truncate text-sm font-semibold">
                            <button type="button" onClick={() => setManageOpen(true)} title="Group chat details: name, people, leave" className="max-w-full truncate rounded text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">{chat.title}</button>
                        </h1>
                    )}
                    <p className="flex min-w-0 text-xs">
                        {isDm && peerNote ? (
                            <>
                                <span className={cn("shrink-0", presenceTextClass(presence))}>{presenceLabel(presence)}</span>
                                <span className="shrink-0 text-[var(--connect-text-3)]">&nbsp;·&nbsp;</span>
                                <StatusNoteText note={peerNote} className="min-w-0 text-[var(--connect-text-2)]" />
                            </>
                        ) : isDm ? (
                            <span className="truncate">
                                <span className={presenceTextClass(presence)}>{presenceLabel(presence)}</span>
                                {chat.peerEmail ? <span className="text-[var(--connect-text-3)]">{` · ${chat.peerEmail}`}</span> : null}
                            </span>
                        ) : (
                            <span className="truncate text-[var(--connect-text-2)]">
                                {`${chat.memberCount} people${others.length > 0 ? ` · ${others.slice(0, 4).map((m) => (m.displayName ?? m.email).split(" ")[0]).join(", ")}${others.length > 4 ? "…" : ""}` : ""}`}
                            </span>
                        )}
                    </p>
                </div>
                <div role="tablist" aria-label="Chat views" className="ml-3.5 hidden items-center gap-3.5 self-stretch sm:flex" onKeyDown={onTabKey}>
                    {tabs.map((t) => (
                        <button
                            key={t.id}
                            id={`chat-tab-${t.id}`}
                            type="button"
                            role="tab"
                            aria-selected={tab === t.id}
                            tabIndex={tab === t.id ? 0 : -1}
                            onClick={() => setTab(t.id)}
                            className={cn(
                                "relative flex h-full items-center text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                                tab === t.id
                                    ? "font-semibold text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:rounded-full after:bg-primary"
                                    : "text-[var(--connect-text-2)] hover:text-foreground"
                            )}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <span className="hidden min-w-0 flex-1 sm:block" aria-hidden />
                {!isDm ? (
                    <button
                        type="button"
                        onClick={() => setManageOpen(true)}
                        aria-label="Group chat details"
                        title="People in this chat"
                        className="flex h-8 shrink-0 items-center gap-1 rounded px-1.5 text-xs text-[var(--connect-text-2)] hover:bg-[var(--connect-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        <UsersRound className="h-4 w-4" />
                        <span className="tabular-nums">{chat.memberCount}</span>
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={() => setAddOpen(true)}
                    aria-label={isDm ? `Add people to a chat with ${chat.title}` : "Add people to this chat"}
                    title="Add people"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-[var(--connect-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                    <UserPlus className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => setFindOpen((v) => !v)}
                    aria-pressed={findOpen}
                    aria-label="Search this chat"
                    title="Search this chat"
                    className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-[var(--connect-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                        findOpen && "bg-[var(--connect-selected)] text-primary"
                    )}
                >
                    <Search className="h-4 w-4" />
                </button>
                <button
                    type="button"
                    onClick={() => void call("video")}
                    disabled={!canCall || calling}
                    aria-label={isDm ? `Connect with ${chat.title} (video call)` : "Connect with everyone in this chat (video call)"}
                    title="Connect (video call)"
                    className="flex h-[30px] shrink-0 items-center gap-1.5 rounded bg-primary px-2 text-[13px] font-semibold text-primary-foreground hover:bg-[var(--connect-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 sm:px-3"
                >
                    <Video className="h-4 w-4" />
                    <span className="hidden sm:inline">Connect</span>
                </button>
                <button
                    type="button"
                    onClick={() => void call("audio")}
                    disabled={!canCall || calling}
                    aria-label={isDm ? `Audio call ${chat.title}` : "Audio call everyone in this chat"}
                    title="Audio call"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-[var(--connect-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
                >
                    <Phone className="h-4 w-4" />
                </button>
            </header>
            {/* Phones: the tabs get their own strip under the header. */}
            <div role="tablist" aria-label="Chat views" className="flex shrink-0 border-b border-[var(--connect-line)] sm:hidden" onKeyDown={onTabKey}>
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.id}
                        tabIndex={tab === t.id ? 0 : -1}
                        onClick={() => setTab(t.id)}
                        className={cn(
                            "flex-1 border-b-2 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary",
                            tab === t.id ? "border-primary font-semibold" : "border-transparent text-muted-foreground"
                        )}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {addOpen ? (
                <AddPeopleDialog
                    chat={chat}
                    memberIds={isDm
                        ? [me, normalizeUserId(chat.peerUserId)]
                        : (members.data ?? []).map((m) => normalizeUserId(m.userId))}
                    onClose={() => setAddOpen(false)}
                />
            ) : null}

            {findOpen ? (
                <ConversationFindBar
                    scope={pinScope}
                    id={chat.id}
                    label={isDm ? `your chat with ${chat.title}` : chat.title}
                    onJump={(hit) => jumpTo(hit.messageId)}
                    onClose={() => setFindOpen(false)}
                />
            ) : null}

            {tab === "files" ? (
                <ConversationFiles scope={isDm ? "dms" : "conversations"} id={chat.id} label={isDm ? `your chat with ${chat.title}` : chat.title} />
            ) : (
            <>
            <PinnedBar scope={pinScope} id={chat.id} onJump={(p) => jumpTo(p.messageId)} />
            {anchor ? (
                <div className="flex shrink-0 items-center gap-2 border-b bg-muted/40 px-4 py-1.5 text-xs" role="status">
                    <History className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    <span className="flex-1 text-muted-foreground">You're viewing older messages.</span>
                    <button type="button" onClick={jumpToLatest} className="font-medium text-primary hover:underline">
                        Jump to latest
                    </button>
                </div>
            ) : null}
            <div className="relative min-h-0 flex-1" {...threadDrop.dropProps}>
                {threadDrop.isDragging ? <DropOverlay label={`Drop files to share with ${chat.title}`} /> : null}
                <div
                    ref={scrollerRef}
                    onScroll={onScroll}
                    role="log"
                    aria-live="polite"
                    aria-label={`Messages with ${chat.title}`}
                    className="h-full overflow-y-auto py-4"
                >
                    {query.isPending ? (
                        <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading messages…
                        </div>
                    ) : query.isError ? (
                        <div className="flex h-full items-center justify-center text-sm text-destructive">
                            Couldn't load this chat. It will retry shortly.
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex h-full flex-col items-center justify-center gap-3 text-center text-sm text-muted-foreground">
                            <ChatAvatar name={chat.title} group={!isDm} />
                            <p>
                                {isDm ? <>This is the start of your chat with <span className="font-medium text-foreground">{chat.title}</span>.</> : "No messages yet."}
                                <br />Say hi 👋
                            </p>
                        </div>
                    ) : (
                        <ol className="flex flex-col">
                            {rows.map((row) => {
                                if (row.type === "day") {
                                    return (
                                        <li key={row.key} className="my-3 flex items-center gap-2.5 px-5 text-xs text-[var(--connect-text-3)]" role="separator">
                                            <span className="h-px flex-1 bg-[var(--connect-line)]" />
                                            {row.label}
                                            <span className="h-px flex-1 bg-[var(--connect-line)]" />
                                        </li>
                                    );
                                }
                                if (row.type === "new") {
                                    return (
                                        <li key={row.key} data-new-divider className="my-2 flex items-center gap-3 px-4 text-[11px] font-semibold text-destructive" role="separator">
                                            <span className="h-px flex-1 bg-destructive/50" />
                                            New messages
                                            <span className="h-px flex-1 bg-destructive/50" />
                                        </li>
                                    );
                                }
                                const { m, mine, first } = row;
                                // "Priya added Alex", "Alex left the chat": a line, not a bubble.
                                if (m.bodyHtml.includes("data-fg-system")) {
                                    return (
                                        <li key={row.key} id={`msg-${m.id}`} className="my-2 px-5 text-center text-xs italic text-[var(--connect-text-3)]">
                                            <span dangerouslySetInnerHTML={{ __html: systemNoteHtml(m.bodyHtml) }} />
                                            <time className="ml-2 not-italic tabular-nums" dateTime={m.sentAt}>{timeLabel(new Date(m.sentAt))}</time>
                                        </li>
                                    );
                                }
                                const name = m.authorDisplayName ?? m.authorEmail;
                                const at = new Date(m.sentAt);
                                return (
                                    <li
                                        key={row.key}
                                        className={cn("group flex gap-2.5 px-3 sm:px-5", first ? "mt-3.5" : "mt-0.5", mine && "flex-row-reverse")}
                                        // A long thread paints and lays out only the rows near the viewport.
                                        style={{ contentVisibility: "auto", containIntrinsicSize: "auto 56px" }}
                                    >
                                        {!mine ? (
                                            first ? <ChatAvatar name={name} size="sm" /> : <span className="w-8 shrink-0" aria-hidden />
                                        ) : null}
                                        <div className={cn("flex min-w-0 max-w-[85%] flex-col sm:max-w-[78%]", mine ? "items-end" : "items-start")}>
                                            {first ? (
                                                // Others: name + time; yours: just the time, right-aligned.
                                                <div className="mb-1.5 flex items-baseline gap-1.5 px-0.5 text-xs text-[var(--connect-text-2)]">
                                                    {!mine ? <span className="font-semibold text-foreground">{name}</span> : null}
                                                    <time className="tabular-nums" dateTime={m.sentAt} title={at.toLocaleString()}>{timeLabel(at)}</time>
                                                </div>
                                            ) : null}
                                            <div
                                                id={`msg-${m.id}`}
                                                className="max-w-full"
                                                title={first ? undefined : at.toLocaleString()}
                                            >
                                                <ChatMessageContent
                                                    scope={isDm ? "dms" : "conversations"}
                                                    containerId={chat.id}
                                                    message={m}
                                                    reactions={reactions.data}
                                                    bubbleClassName={cn(
                                                        "rounded-lg px-3 py-2",
                                                        mine ? "bg-[var(--connect-primary-soft)]" : "bg-[var(--connect-subtle)]"
                                                    )}
                                                    pin={{
                                                        pinned: pinnedIds.has(m.id),
                                                        onToggle: () => pinToggle.mutate({ messageId: m.id, pin: !pinnedIds.has(m.id) })
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                </div>
                {showJump || newBelow > 0 ? (
                    <button
                        type="button"
                        onClick={jumpToLatest}
                        className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-xs font-medium shadow-md hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                        {newBelow > 0 ? `${newBelow} new message${newBelow === 1 ? "" : "s"}` : "Jump to latest"}
                        <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                ) : null}
            </div>

            <div className="flex h-5 shrink-0 items-center gap-1.5 px-5 text-xs text-[var(--connect-text-3)]" aria-live="polite">
                {typing ? (
                    <>
                        <span className="connect-typing-dots" aria-hidden><span /><span /><span /></span>
                        {typing}
                    </>
                ) : null}
            </div>
            {peerNote?.showWhenMessaged ? (
                <div className="mx-3 mb-1 flex min-w-0 items-center gap-2 rounded-md border bg-muted/50 px-3 py-1.5 text-xs" role="note">
                    <MessageSquareText className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                    <span className="shrink-0 font-medium">{chat.title}:</span>
                    <StatusNoteText note={peerNote} className="min-w-0" />
                </div>
            ) : null}
            <ChatComposer
                placeholder={isDm ? `Message ${chat.title}` : "Type a message"}
                onSend={send}
                onTyping={onTyping}
                attachments={attachments}
                resolveShare={resolveShare}
                mentionCandidates={mentionCandidates}
                poll={{ scope: pinScope, conversationId: chat.id }}
                draftKey={`${chat.kind}:${chat.id}`}
                everyone={!isDm}
                importanceFlags
                onEditLast={() => {
                    // The newest message of mine that isn't a poll or a system note.
                    const last = [...messages].reverse().find((m) => normalizeUserId(m.authorUserId) === me && !m.bodyHtml.includes("data-fg-system"));
                    if (last) window.dispatchEvent(new CustomEvent("fg:edit-message", { detail: { id: last.id } }));
                }}
            />
            </>
            )}
            {manageOpen && !isDm ? (
                <GroupManageDialog
                    chat={chat}
                    me={me}
                    onClose={() => setManageOpen(false)}
                    onLeft={() => { setManageOpen(false); onBack(); }}
                />
            ) : null}
        </section>
    );
}

/** A system note's text only: the server writes it as `<p data-fg-system><em>…</em></p>` with the names HTML-encoded. */
function systemNoteHtml(html: string): string {
    const inner = /<em>([\s\S]*?)<\/em>/.exec(html)?.[1] ?? "";
    // Only the tags the server writes (<strong>) survive; everything else is dropped.
    return inner.replace(/<(?!\/?strong>)[^>]*>/g, "");
}
